import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, of } from 'rxjs';
import { catchError, map, shareReplay, take } from 'rxjs/operators';

import { JwtAuthService } from './auth/jwt-auth.service';
import { AppConfirmService } from './app-confirm/app-confirm.service';

/**
 * Detecta quando a sessão está rodando na área CENTRAL (empresa Infokaw, filial 1 da nuvem) em vez
 * da empresa do usuário, e avisa com clareza.
 *
 * Contexto: qualquer falha no login remoto (empresa fora do ar, timeout, captcha) fazia o backend
 * carimbar a filial 1 como "última empresa usada", jogando o usuário na Infokaw de forma permanente.
 * A causa foi corrigida no AuthController, mas sessões antigas e outros caminhos ainda podem cair
 * na central — e o único sinal que o usuário tinha era o genérico "contate o Administrador".
 */
@Injectable({ providedIn: 'root' })
export class EmpresaContextService {

  /** Filial da área central (Infokaw) na nuvem. */
  private static readonly FILIAL_CENTRAL = '1';

  /** Marca "continuar assim" para não insistir com quem trabalha na Infokaw de propósito. */
  private static readonly IGNORAR_KEY = 'EMPRESA_CONTEXTO_CENTRAL_IGNORADO';

  private promptAberto = false;
  private outraEmpresaCache$: Observable<boolean> = null;

  private chaveSessao: string = null;

  constructor(
    private jwtAuth: JwtAuthService,
    private appConfirmService: AppConfirmService,
    private router: Router
  ) {
    // trocou de usuário ou de empresa: cache e "continuar assim" deixam de valer
    this.jwtAuth.item$.subscribe((item) => {
      const chave = `${item?.userName ?? ''}|${item?.filialId ?? ''}|${item?.empresaRemota?.id ?? ''}`;
      if (this.chaveSessao !== null && this.chaveSessao !== chave) {
        this.reset();
      }
      this.chaveSessao = chave;
    });
  }

  /**
   * true quando a sessão está na empresa central (Infokaw) da NUVEM. Na intranet a filial 1 é a
   * própria empresa do cliente, então lá nunca dispara.
   */
  isContextoCentral(): boolean {
    if (this.jwtAuth.isIntranet) {
      return false;
    }
    const item = this.jwtAuth.item$?.value;
    if (!item) {
      return false;
    }
    return !item.remoto
      && `${item.filialId ?? ''}` === EmpresaContextService.FILIAL_CENTRAL;
  }

  /**
   * true quando o usuário está na central SEM acesso efetivo lá — o caso que o aviso existe para
   * resolver.
   *
   * O login cria automaticamente um vínculo ROLE_USER na filial 1 para todo usuário cadastrado na
   * central (AuthController: usuarioSecretFilialCommandService.create(..., "ROLE_USER", ...)). Quem
   * só tem essa role é justamente quem caiu na Infokaw sem poder fazer nada. Já quem tem ROLE_ROOT,
   * ROLE_ADMIN ou qualquer módulo (VENDAS, FATURAMENTO, FINANCEIRO, AGENDA, ESTOQUE...) trabalha na
   * central de propósito — esse nunca deve ser incomodado.
   */
  semAcessoEfetivoNaCentral(): boolean {
    const roles: any[] = this.jwtAuth.item$?.value?.roles ?? [];
    if (!roles.length) {
      return true;
    }
    // roles chegam como 'ROLE_X' ou 'ROLE_X_WRITE/READ/DELETE/PRINT'
    return !roles.some((role) => {
      const nome = `${role ?? ''}`.trim().toUpperCase();
      return nome.startsWith('ROLE_') && !nome.startsWith('ROLE_USER');
    });
  }

  /**
   * Sessão presa na central sem acesso liberado lá. Diferente de {@link deveAvisarEmpresaIncorreta},
   * NÃO considera o "continuar assim": quem está aqui não deve ver dados da central (notificações,
   * avisos internos) nem depois de dispensar o diálogo.
   */
  isCentralSemAcesso(): boolean {
    return this.isContextoCentral() && this.semAcessoEfetivoNaCentral();
  }

  /**
   * Gate único do aviso, usado pelo guard e pelo interceptor: sessão na central + usuário sem acesso
   * lá + ainda não pediu para continuar assim.
   */
  deveAvisarEmpresaIncorreta(): boolean {
    return this.isCentralSemAcesso() && !this.ignorado();
  }

  /**
   * true se o usuário tem alguma OUTRA empresa além da central — cacheado por sessão.
   * "Outra" = qualquer empresa remota, ou qualquer filial local que não seja a filial 1 (Infokaw).
   */
  temOutraEmpresa$(): Observable<boolean> {
    if (!this.outraEmpresaCache$) {
      this.outraEmpresaCache$ = this.jwtAuth.getEmpresaList().pipe(
        map((empresas) => (empresas ?? []).some((e: any) =>
          !!e?.remoto || `${e?.id ?? ''}` !== EmpresaContextService.FILIAL_CENTRAL)),
        catchError(() => of(false)),
        shareReplay(1)
      );
    }
    return this.outraEmpresaCache$.pipe(take(1));
  }

  /** Usuário optou por continuar na central nesta sessão. */
  ignorado(): boolean {
    try {
      return sessionStorage.getItem(EmpresaContextService.IGNORAR_KEY) === 'true';
    } catch {
      return false;
    }
  }

  private marcaIgnorado(): void {
    try {
      sessionStorage.setItem(EmpresaContextService.IGNORAR_KEY, 'true');
    } catch {
      // sessionStorage indisponível: apenas não persiste a escolha
    }
  }

  /** Limpa o cache/escolha — chamar ao trocar de empresa ou sair. */
  reset(): void {
    this.outraEmpresaCache$ = null;
    try {
      sessionStorage.removeItem(EmpresaContextService.IGNORAR_KEY);
    } catch {
      // ignora
    }
  }

  /**
   * Abre o alerta de empresa errada e leva para a seleção de empresa.
   * @param origem apenas para log ('guard' | '403' | ...)
   */
  promptEmpresaIncorreta(origem: string): void {
    if (this.promptAberto || !this.deveAvisarEmpresaIncorreta()) {
      return;
    }
    this.promptAberto = true;
    console.warn(`[EmpresaContexto] sessão na empresa central (Infokaw) — origem: ${origem}`);

    const filial = this.jwtAuth.filial$?.value;
    const nomeAtual = (filial?.nomeFantasia || filial?.razaoSocial || 'INFOKAW').toString().toUpperCase();

    this.appConfirmService.confirm({
      title: 'Verifique a empresa selecionada',
      message:
        `Você está conectado na empresa ${nomeAtual} (área central), e não na sua empresa.\n\n` +
        'Seu usuário não tem acessos liberados nesta empresa — por isso o sistema recusa as ' +
        'operações e não mostra os seus dados.\n\n' +
        'Selecione a empresa correta para continuar.',
      error: true,
      icon: 'business',
      okText: 'Selecionar empresa',
      cancelText: 'Continuar assim',
      // sem isto o AppConfirmService esconde o cancelar quando error = true
      disableCancel: false
    }).subscribe((confirmado) => {
      this.promptAberto = false;
      if (confirmado) {
        this.router.navigateByUrl('/profile/filial');
        return;
      }
      this.marcaIgnorado();
    });
  }
}
